import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';

import { AuthGuard } from '@/components/guards/AuthGuard';
import { RoleGuard } from '@/components/guards/RoleGuard';
import { AppShell } from '@/components/layout/AppShell';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { PageLoader } from '@/components/ui/page-loader';
import { ErrorBoundary } from '@/components/ui/error-boundary';

const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const EmployeesPage = lazy(() => import('@/pages/employees/EmployeesPage'));
const EmployeeDetailPage = lazy(() => import('@/pages/employees/EmployeeDetailPage'));
const AttendancePage = lazy(() => import('@/pages/attendance/AttendancePage'));
const LeavesPage = lazy(() => import('@/pages/leaves/LeavesPage'));
const PayrollPage = lazy(() => import('@/pages/payroll/PayrollPage'));
const DepartmentsPage = lazy(() => import('@/pages/departments/DepartmentsPage'));
const ShiftsPage = lazy(() => import('@/pages/shifts/ShiftsPage'));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));
const HolidaysPage = lazy(() => import('@/pages/holidays/HolidaysPage'));
const MyLeavesPage = lazy(() => import('@/pages/my-leaves/MyLeavesPage'));
const RegularisationPage = lazy(() => import('@/pages/regularisation/RegularisationPage'));
const DirectoryPage = lazy(() => import('@/pages/directory/DirectoryPage'));
const MyPayslipsPage = lazy(() => import('@/pages/my-payslips/MyPayslipsPage'));
const ReportsPage = lazy(() => import('@/pages/reports/ReportsPage'));
const SalaryStructurePage = lazy(() => import('@/pages/salary/SalaryStructurePage'));
const AnnouncementsPage = lazy(() => import('@/pages/announcements/AnnouncementsPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const ChangePasswordPage = lazy(() => import('@/pages/auth/ChangePasswordPage'));
const NotFoundPage = lazy(() => import('@/pages/errors/NotFoundPage'));
const ApprovalInboxPage = lazy(() => import('@/pages/approval-inbox/ApprovalInboxPage'));
const MyLettersPage = lazy(() => import('@/pages/my-letters/MyLettersPage'));
const ReimbursementsPage = lazy(() => import('@/pages/reimbursements/ReimbursementsPage'));

// Hidden for the field launch (code kept): Org Chart, HR Policies, Office Locations.
// Their routes redirect to the dashboard; to bring one back, restore its lazy import + route.
//   OrgChartPage        → @/pages/org-chart/OrgChartPage
//   HRPolicyPage        → @/pages/hr-policy/HRPolicyPage
//   OfficeLocationsPage → @/pages/office-locations/OfficeLocationsPage

function Lazy({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

export const router = createBrowserRouter([
  {
    // Sign-in, reset and first-login password screens share the brand panel; moving
    // between them only swaps the card (AuthLayout holds the Suspense boundary)
    element: <AuthLayout />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
      {
        // Signed-in but outside the app shell: forced first-login change lands here
        path: '/change-password',
        element: (
          <AuthGuard>
            <ChangePasswordPage />
          </AuthGuard>
        ),
      },
    ],
  },
  {
    path: '/',
    element: (
      <AuthGuard>
        <AppShell />
      </AuthGuard>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      {
        path: 'dashboard',
        element: (
          <Lazy>
            <DashboardPage />
          </Lazy>
        ),
      },
      {
        path: 'employees',
        element: (
          <RoleGuard allow={['SUPER_ADMIN', 'ORG_ADMIN', 'HR', 'MANAGER']}>
            <Lazy>
              <EmployeesPage />
            </Lazy>
          </RoleGuard>
        ),
      },
      {
        path: 'employees/:id',
        element: (
          <RoleGuard allow={['SUPER_ADMIN', 'ORG_ADMIN', 'HR', 'MANAGER']}>
            <Lazy>
              <EmployeeDetailPage />
            </Lazy>
          </RoleGuard>
        ),
      },
      {
        path: 'attendance',
        element: (
          <Lazy>
            <AttendancePage />
          </Lazy>
        ),
      },
      {
        path: 'leaves',
        element: (
          <RoleGuard allow={['SUPER_ADMIN', 'ORG_ADMIN', 'HR', 'MANAGER']}>
            <Lazy>
              <LeavesPage />
            </Lazy>
          </RoleGuard>
        ),
      },
      {
        path: 'payroll',
        element: (
          <RoleGuard allow={['SUPER_ADMIN', 'ORG_ADMIN', 'HR']}>
            <Lazy>
              <PayrollPage />
            </Lazy>
          </RoleGuard>
        ),
      },
      {
        path: 'salary-structure',
        element: (
          <RoleGuard allow={['SUPER_ADMIN', 'ORG_ADMIN', 'HR']}>
            <Lazy>
              <SalaryStructurePage />
            </Lazy>
          </RoleGuard>
        ),
      },
      {
        path: 'departments',
        element: (
          <RoleGuard allow={['SUPER_ADMIN', 'ORG_ADMIN', 'HR']}>
            <Lazy>
              <DepartmentsPage />
            </Lazy>
          </RoleGuard>
        ),
      },
      {
        path: 'shifts',
        element: (
          <RoleGuard allow={['SUPER_ADMIN', 'ORG_ADMIN', 'HR']}>
            <Lazy>
              <ShiftsPage />
            </Lazy>
          </RoleGuard>
        ),
      },
      {
        path: 'holidays',
        element: (
          <Lazy>
            <HolidaysPage />
          </Lazy>
        ),
      },
      {
        path: 'my-leaves',
        element: (
          <Lazy>
            <MyLeavesPage />
          </Lazy>
        ),
      },
      {
        path: 'regularisation',
        element: (
          <Lazy>
            <RegularisationPage />
          </Lazy>
        ),
      },
      {
        path: 'approval-inbox',
        element: (
          <RoleGuard allow={['SUPER_ADMIN', 'ORG_ADMIN', 'HR', 'MANAGER']}>
            <Lazy>
              <ApprovalInboxPage />
            </Lazy>
          </RoleGuard>
        ),
      },
      {
        path: 'directory',
        element: (
          <Lazy>
            <DirectoryPage />
          </Lazy>
        ),
      },
      { path: 'org-chart', element: <Navigate to="/dashboard" replace /> },
      {
        path: 'settings',
        element: (
          <Lazy>
            <SettingsPage />
          </Lazy>
        ),
      },
      { path: 'hr-policies', element: <Navigate to="/dashboard" replace /> },
      {
        path: 'my-payslips',
        element: (
          <Lazy>
            <MyPayslipsPage />
          </Lazy>
        ),
      },
      {
        path: 'reimbursements',
        element: (
          <Lazy>
            <ReimbursementsPage />
          </Lazy>
        ),
      },
      {
        path: 'my-letters',
        element: (
          <Lazy>
            <MyLettersPage />
          </Lazy>
        ),
      },
      {
        path: 'reports',
        element: (
          <RoleGuard allow={['SUPER_ADMIN', 'ORG_ADMIN', 'HR']}>
            <Lazy>
              <ReportsPage />
            </Lazy>
          </RoleGuard>
        ),
      },
      {
        path: 'announcements',
        element: (
          <Lazy>
            <AnnouncementsPage />
          </Lazy>
        ),
      },
      { path: 'office-locations', element: <Navigate to="/dashboard" replace /> },
    ],
  },
  {
    path: '*',
    element: (
      <Lazy>
        <NotFoundPage />
      </Lazy>
    ),
  },
]);
